import React, { useCallback, useEffect, useState } from "react";
// @ts-ignore
import "../css/app.css";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import HomePage from "./screens/homePage";
import ProductsPage from "./screens/productsPage";
import { OrdersPage } from "./screens/ordersPage";
import { UserPage } from "./screens/userPage";
import { HelpPage } from "./screens/helpPage";
import HomeNavbar from "./components/headers/HomeNavbar";
import OtherNavbar from "./components/headers/OtherNavbar";
import Footer from "./components/footer";
import AuthenticationModal from "./components/auth";
import useCart from "./hooks/useCart";
import MemberService from "./services/MemberService";
import { Member } from "../lib/types/member";
import OrderService from "./services/OrderService";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../lib/sweetAlert";

const MEMBER_STORAGE_KEY = "memberData";

const readStoredMember = (): Member | null => {
  try {
    const storedMember = localStorage.getItem(MEMBER_STORAGE_KEY);
    if (!storedMember) return null;

    const parsedMember: unknown = JSON.parse(storedMember);
    if (!parsedMember || typeof parsedMember !== "object") {
      localStorage.removeItem(MEMBER_STORAGE_KEY);
      return null;
    }

    const candidate = parsedMember as { _id?: unknown; memberNick?: unknown };
    if (typeof candidate._id !== "string" || typeof candidate.memberNick !== "string") {
      localStorage.removeItem(MEMBER_STORAGE_KEY);
      return null;
    }

    return parsedMember as Member;
  } catch {
    localStorage.removeItem(MEMBER_STORAGE_KEY);
    return null;
  }
};

function App() {
  const location = useLocation();
  const history = useHistory();
  const cart = useCart();
  const [authMember, setAuthMember] = useState<Member | null>(readStoredMember);
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  const openSignup = useCallback(() => {
    setLoginOpen(false);
    setSignupOpen(true);
  }, []);
  const openLogin = useCallback(() => {
    setSignupOpen(false);
    setLoginOpen(true);
  }, []);
  const closeAuthentication = useCallback(() => {
    setSignupOpen(false);
    setLoginOpen(false);
  }, []);

  const handleAuthenticated = useCallback((member: Member) => {
    localStorage.setItem(MEMBER_STORAGE_KEY, JSON.stringify(member));
    setAuthMember(member);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await new MemberService().logout();
    } catch {
      // Clear local state even if the session has already expired server-side.
      localStorage.removeItem(MEMBER_STORAGE_KEY);
    }
    setAuthMember(null);
  }, []);

  const handleCheckout = useCallback(async () => {
    if (!authMember) {
      openLogin();
      return;
    }
    if (!cart.cartItems.length) return;
    try {
      await new OrderService().createOrder(cart.cartItems);
      cart.onDeleteAll();
      history.push("/orders");
      await sweetTopSmallSuccessAlert("Order created successfully");
    } catch (error) {
      await sweetErrorHandling(error);
    }
  }, [authMember, cart, history, openLogin]);

  const navigationProps = {
    authMember,
    cart,
    onLogin: openLogin,
    onSignup: openSignup,
    onLogout: handleLogout,
    onCheckout: handleCheckout,
  };

  return (
    <div className="coco-app-shell">
      {location.pathname === "/" ? <HomeNavbar {...navigationProps} /> : <OtherNavbar {...navigationProps} />}
      <main className="coco-app-shell__main">
        <Switch>
          <Route path="/products"><ProductsPage cart={cart} /></Route>
          <Route path="/orders"><OrdersPage authMember={authMember} /></Route>
          <Route path="/member-page"><UserPage authMemberId={authMember?._id ?? null} onMemberUpdated={handleAuthenticated} /></Route>
          <Route path="/help"><HelpPage /></Route>
          <Route exact path="/"><HomePage /></Route>
        </Switch>
      </main>
      <Footer />
      <AuthenticationModal
        open={signupOpen || loginOpen}
        mode={signupOpen ? "signup" : "login"}
        onClose={closeAuthentication}
        onRequestSignup={openSignup}
        onRequestLogin={openLogin}
        onAuthenticated={handleAuthenticated}
      />
    </div>
  );
}

export default App;
